/**
 * DUP-3 (Daily Undulating Periodization, 3-rotation)
 * ===================================================
 *
 * Evidence: Rhea et al. 2002 (J Strength Cond Res 16(2)) showed that
 * varying load + rep range across same-week sessions outperformed
 * linear periodization for strength + size gains in trained lifters.
 *
 * DESIGN (rewritten May 28, 2026 after user feedback):
 * The previous version had a global `plan.dupRotation` that advanced
 * once-per-calendar-day on Set 1 done. That broke this scenario:
 *   "I had a 3×12 day. I logged it. The app refreshed and the same
 *    exercise was now a 6-rep day — I never asked for that."
 *
 * The user's real intent is:
 *   • Week 1: each training weekday is MANUALLY assigned a rep range
 *     (e.g. Mon = 12-rep, Wed = 6-rep, Fri = 25-rep). The setup dialog
 *     captures today's weight, reps, sets AND the 3 rep ranges (default
 *     12/6/25, custom allowed).
 *   • Week 2+: same weekday automatically reuses its assigned rep range.
 *     Weights evolve silently after every Set 1 via a WEIGHT-AWARE
 *     observation rule (Feb 2026 redesign — see below).
 *
 * STORAGE MODEL (Feb 2026, v3 — per-(exercise, rep-range)):
 *   plan.dupRepRanges:        [number, number, number]
 *                             e.g. [12, 6, 25] or user's custom.
 *   plan.dupWeekdaySchedule:  Record<dayKey, number>
 *                             day name → target reps.
 *   plan.dupExerciseWeights:  Record<exerciseName, Record<targetRepsStr,
 *                              { weight, inc, lastUpdated }>>
 *                             CANONICAL STORE: the user's working weight
 *                             per (exercise name, target rep range).
 *                             Each rep range evolves INDEPENDENTLY based
 *                             on its own Set 1. No Epley cross-projection.
 *   plan.dupExerciseRm12:     (legacy, v2) — read-only fallback during
 *                             migration. Migrated to dupExerciseWeights
 *                             at plan load and then ignored.
 *   exercise.dup:             { enabled, mode, targetReps, unit,
 *                                rm12 (oldest legacy), lastUpdated }
 *
 * WEIGHT UPDATE RULE (v3, after real-user audit Feb 16, 2026):
 *   Set 1 done with (actualWeight, actualReps), targetReps known:
 *     adj = mannAdjustment(targetReps, actualReps, perExerciseInc)
 *     if adj === 0:  next weight = actualWeight  // honor manual override
 *     else:          next weight = round(actualWeight + adj, inc)
 *
 * The Mann reps table itself is unchanged (the user confirmed it's
 * "OK and not aggressive" across 6/12/25-rep targets). The KEY bug
 * fixes vs v1/v2 are:
 *   1. Anchor on actualWeight (not stored projection) — fixes the
 *      "I dialed weight down, algo bumped anyway" bug.
 *   2. Per-rep-range storage — fixes the "Back Squat 52.5 → 50 → 47.5"
 *      Epley round-trip drift that came from compressing 6/12/25-rep
 *      working weights into a single rm12 with coarse increments.
 *   3. adj===0 case: stick to the user's actual lifted weight exactly
 *      — fixes the "I lifted 23, algo prescribes 22.5" half-step bug.
 */

const KG_INCREMENT = 2.5;
const LB_INCREMENT = 5;

import { getExerciseIncrement, roundToIncrement, Unit } from './loadIncrement';
import { effectiveReps as _effReps } from './partialReps';

export const DEFAULT_REP_RANGES: [number, number, number] = [12, 6, 25];

export interface DupConfig {
  enabled: boolean;
  mode: 'dup-3';
  rm12: number;             // canonical 12RM in user's unit (back-calc'd from user input)
  unit?: 'kg' | 'lb';
  /** Target reps the user assigned to THIS exercise during setup. Per-
   *  exercise override of `plan.dupWeekdaySchedule[dayKey]`. Was added
   *  Feb 2026 after a user reported wrong weights on Back Squat / rear
   *  delt flyes — the day-level schedule (one value per day) was being
   *  overwritten by every subsequent DUP setup on the same day. Per-
   *  exercise storage means each lift carries its own target rep range
   *  regardless of how many other DUP exercises share the day. */
  targetReps?: number;
  lastUpdated?: string;
}

function roundToPlate(weight: number, unit: 'kg' | 'lb' = 'kg', inc?: number): number {
  const step = inc && inc > 0 ? inc : (unit === 'lb' ? LB_INCREMENT : KG_INCREMENT);
  return Math.max(step, Math.round(weight / step) * step);
}

/** Local YYYY-MM-DD date stamp. */
function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local weekday key "0" (Sun) – "6" (Sat). KEPT FOR BACK-COMPAT only —
 *  the schedule is now keyed by the training day name string ("Mon"/"Tue"/
 *  "Wed"/"Thu"/"Fri"/"Sat"/"Sun") that the rest of the workout UI already
 *  uses for `plan.dayExercises`. Using the same string avoids the bug where
 *  setting up Friday's exercise on a Thursday wrote into the "Thu" slot. */
export function todayWeekdayKey(): string {
  return String(new Date().getDay());
}

/** Rep ranges configured on the plan (defaulted if missing). */
export function getPlanRepRanges(plan: any): [number, number, number] {
  const r = plan?.dupRepRanges;
  if (Array.isArray(r) && r.length === 3 && r.every(n => typeof n === 'number' && n > 0)) {
    return [r[0], r[1], r[2]];
  }
  return [...DEFAULT_REP_RANGES];
}

/** Resolve the target reps for a specific exercise on `dayKey`.
 *
 *  Priority:
 *    1. `exercise.dup.targetReps` — the per-exercise override set during
 *       the DUP setup dialog. ALWAYS wins when present.
 *    2. `plan.dupWeekdaySchedule[dayKey]` — legacy day-level schedule
 *       (one value per day, shared across exercises). Kept as a fallback
 *       so plans created before Feb 2026 self-heal.
 *    3. **Self-heal**: peek at this exercise's Set 2 reps. In a DUP-3
 *       exercise Set 1 is AMRAP ('max') and Set 2+ store the target —
 *       so the current set state itself encodes the target reps for
 *       legacy plans where neither (1) nor (2) is populated. This makes
 *       the algorithm robust against pre-v5.68 plans (or plans whose
 *       schedule was nulled by an older migration). Reps cleared by a
 *       week-transition pass through this branch as undefined and
 *       fall through to (4).
 *    4. Plan default rep range (Hypertrophy 12 reps).
 *
 *  Returns null only when EVERY source above is empty — meaning the user
 *  has never configured DUP for this day at all.
 */
export function getDupTargetRepsForExercise(plan: any, exercise: any, dayKey?: string): number | null {
  // 1. Per-exercise override (set in the DUP setup dialog).
  const exReps = Number(exercise?.dup?.targetReps);
  if (exReps > 0) return exReps;
  // 2. Plan-level day schedule.
  if (dayKey) {
    const dayReps = getDupTargetRepsForDay(plan, dayKey);
    if (dayReps && dayReps > 0) return dayReps;
  }
  // 3. Self-heal: infer from Set 2 reps. Only useful BEFORE the week
  //    transition has wiped reps; after a wipe sets[1].reps === '' and
  //    we fall through. Callers in the week-transition path should call
  //    `inferAndPersistDupTargetReps` BEFORE clearing the sets.
  const setReps = exercise?.sets?.[1]?.reps;
  if (setReps && setReps !== 'max') {
    const inferred = parseInt(String(setReps), 10);
    if (inferred > 0) return inferred;
  }
  return null;
}

/** Self-heal helper meant to run BEFORE the week-transition wipes reps.
 *
 *  For a DUP-enabled exercise that lacks `exercise.dup.targetReps`, this
 *  reads the target from the current Set 2 reps field (DUP convention:
 *  Set 1 is AMRAP, Set 2+ carry the target) and writes it onto the
 *  exercise so the post-wipe `applyDupToExercise` can find it again.
 *  Mutates in-place. Returns true if a heal happened. */
export function inferAndPersistDupTargetReps(exercise: any): boolean {
  const cfg = exercise?.dup;
  if (!cfg || !cfg.enabled) return false;
  if (Number(cfg.targetReps) > 0) return false; // already set
  const set2Reps = exercise?.sets?.[1]?.reps;
  if (!set2Reps || set2Reps === 'max') return false;
  const inferred = parseInt(String(set2Reps), 10);
  if (!inferred || inferred <= 0) return false;
  exercise.dup = { ...cfg, targetReps: inferred };
  return true;
}

/** Target reps assigned to a given training day (e.g. "Fri") on this
 *  plan, or null if unassigned.
 *
 *  Accepts ALL three historical key formats so existing plans self-heal
 *  without a migration:
 *    • lowercase day name   — "mon"/"tue"/"wed"/"thu"/"fri"/"sat"/"sun"
 *      (current format, used by the workout pane's `dayExercises` keys)
 *    • Pascal-case day name — "Mon"/"Tue"/...  (early-2026 test fixtures)
 *    • system-weekday digit — "0"=Sun..."6"=Sat
 *      (pre-May 2026 format — bug fix [iter146]: production users on the
 *      old format had the DUP-3 badge silently disappear after week 1
 *      because the new lookup format missed their stored schedule.)
 */
export function getDupTargetRepsForDay(plan: any, dayKey: string): number | null {
  if (!dayKey) return null;
  const schedule = plan?.dupWeekdaySchedule;
  if (!schedule) return null;

  // 1. Try the key as-is (current lowercase format)
  const direct = schedule[dayKey];
  if (typeof direct === 'number' && direct > 0) return direct;

  // 2. Try Pascal-case ("Thu") — early-2026 test fixture format
  const pascal = dayKey.charAt(0).toUpperCase() + dayKey.slice(1).toLowerCase();
  const pascalHit = schedule[pascal];
  if (typeof pascalHit === 'number' && pascalHit > 0) return pascalHit;

  // 3. Legacy: system-weekday digit ("0"=Sun ... "6"=Sat)
  const dayToDigit: Record<string, string> = {
    sun: '0', mon: '1', tue: '2', wed: '3', thu: '4', fri: '5', sat: '6',
  };
  const digit = dayToDigit[dayKey.toLowerCase()];
  if (digit !== undefined) {
    const legacy = schedule[digit];
    if (typeof legacy === 'number' && legacy > 0) return legacy;
  }
  return null;
}

/**
 * @deprecated kept only so the old test imports compile. The new code path
 * is `getDupTargetRepsForDay(plan, dayKey)` — pass the actual training day
 * key (e.g. "Fri") the user is logging, not the system date.
 */
export function getDupTargetRepsForWeekday(plan: any, weekday: string): number | null {
  return getDupTargetRepsForDay(plan, weekday);
}

/**
 * @deprecated DO NOT use — silently reads the schedule for today's
 * SYSTEM weekday, which is wrong when the user is viewing a future/past
 * training day. Replaced by `getDupTargetRepsForDay(plan, dayKey)`.
 * Kept temporarily for back-compat with very old callers.
 */
export function getDupTargetRepsForToday(plan: any): number | null {
  return getDupTargetRepsForDay(plan, todayWeekdayKey());
}

/**
 * Epley-style RM converter. Given a load × reps performed, back-calc the
 * weight the lifter could lift for `targetReps` reps. Used to:
 *   (a) Convert the user's first-session input → canonical 12RM (rm12)
 *   (b) Project rm12 → working weight at any other rep target.
 *
 * Formula: 1RM = w × (1 + r/30), and X-RM = 1RM / (1 + X/30).
 */
function convertRepMax(weight: number, fromReps: number, toReps: number): number {
  if (weight <= 0 || fromReps <= 0 || toReps <= 0) return 0;
  const oneRm = weight * (1 + fromReps / 30);
  return oneRm / (1 + toReps / 30);
}

/** Compute working weight for `targetReps` given the canonical 12RM. */
export function dupWorkingWeight(rm12: number, targetReps: number, unit: 'kg' | 'lb' = 'kg', inc?: number): number {
  if (!rm12 || rm12 <= 0 || !targetReps || targetReps <= 0) return 0;
  return roundToPlate(convertRepMax(rm12, 12, targetReps), unit, inc);
}

/** Back-calc canonical 12RM from a user-entered (weight, reps) pair. */
export function rm12FromUserInput(weight: number, reps: number, unit: 'kg' | 'lb' = 'kg', inc?: number): number {
  return roundToPlate(convertRepMax(weight, reps, 12), unit, inc);
}

/**
 * Mann-style AMRAP adjustment, parameterised by TARGET reps. After Set 1
 * the lifter's `repsPerformed` versus target reveals whether the load
 * is too heavy/light, and we shift the canonical 12RM by ±increment.
 *
 * The thresholds expand with target reps so endurance days don't whip-saw
 * on every extra rep:
 *   short (≤8 target):  ±2 from target ⇒ 0    · ≤−3 ⇒ −inc · ≥+3 ⇒ +inc · ≥+5 ⇒ +2inc
 *   medium (9-15):       ±2          ⇒ 0    · ≤−5 ⇒ −2inc · ≥+3 ⇒ +inc · ≥+6 ⇒ +2inc
 *   long (≥16):         ±3          ⇒ 0    · ≤−5 ⇒ −inc · ≥+4 ⇒ +inc · ≥+8 ⇒ +2inc
 *
 * `inc` is the exercise's per-equipment smallest loadable increment
 * (defaults to 2.5 kg / 5 lb if not provided).
 */
export function dupAdjustment(targetReps: number, repsPerformed: number, unit: 'kg' | 'lb' = 'kg', inc?: number): number {
  const step = inc && inc > 0 ? inc : (unit === 'lb' ? LB_INCREMENT : KG_INCREMENT);
  if (repsPerformed <= 0 || targetReps <= 0) return 0;
  const delta = repsPerformed - targetReps;
  if (targetReps <= 8) {
    if (delta <= -3) return -step;
    if (delta <= 2) return 0;
    if (delta <= 4) return step;
    return step * 2;
  }
  if (targetReps <= 15) {
    if (delta <= -5) return -step * 2;
    if (delta <= -3) return -step;
    if (delta <= 1) return 0;
    if (delta <= 4) return step;
    return step * 2;
  }
  // long (endurance)
  if (delta <= -5) return -step;
  if (delta <= 3) return 0;
  if (delta <= 7) return step;
  return step * 2;
}

/** Compute next-session canonical 12RM after Set 1 reps logged.
 *  LEGACY (pre-Feb 2026): trusted the prescribed weight + Mann delta on
 *  reps alone. Kept exported so older unit tests still pin the Mann
 *  table. New code paths use `dupNewRm12FromActual()` (weight-aware). */
export function dupNewRm12(currentRm12: number, targetReps: number, set1Reps: number, unit: 'kg' | 'lb' = 'kg', inc?: number): number {
  const step = inc && inc > 0 ? inc : (unit === 'lb' ? LB_INCREMENT : KG_INCREMENT);
  const adj = dupAdjustment(targetReps, set1Reps, unit, step);
  // Adjust at the rep range the user actually trained, then re-anchor to 12RM.
  const trainedWeight = dupWorkingWeight(currentRm12, targetReps, unit, step);
  const newTrainedWeight = Math.max(step, trainedWeight + adj);
  return rm12FromUserInput(newTrainedWeight, targetReps, unit, step);
}

/**
 * Weight-aware update (Feb 2026 redesign, v2 after real-user audit).
 *
 * Compute the canonical 12RM for the NEXT session, using TWO ground
 * truths from the actual logged Set 1:
 *   • `actualWeight` — what the user actually had on the bar/handle/
 *     pin stack, NOT what the previous prescription said. If the user
 *     manually dialed weight up or down, we honor reality.
 *   • `actualReps`  — AMRAP performance against `targetReps`.
 *
 * Rule:
 *   1. Compute Mann's reps-based adjustment in this exercise's smallest
 *      loadable increment (`inc`). Mann's table itself is unchanged —
 *      the user accepted it as "OK and not aggressive" across 6/12/25-
 *      rep targets.
 *   2. If Mann adj === 0 (user landed in the green "no change" zone):
 *      return `currentRm12` EXACTLY. Do NOT round-trip through
 *      rm12FromUserInput → dupWorkingWeight, because the round-trip
 *      silently loses ~1 inc per session with coarse increments (the
 *      "Back Squat 52.5 → 50 → 47.5 each week" drift bug).
 *   3. If Mann adj !== 0: anchor the new working weight on
 *      `actualWeight + adj`, then back-convert to a 12RM. This is what
 *      kills the original bug — if the user dialed weight DOWN but
 *      still hit target reps, the algorithm sees the lower load.
 */
export function dupNewRm12FromActual(
  currentRm12: number,
  actualWeight: number,
  actualReps: number,
  targetReps: number,
  unit: 'kg' | 'lb' = 'kg',
  inc?: number,
): number {
  const step = inc && inc > 0 ? inc : (unit === 'lb' ? LB_INCREMENT : KG_INCREMENT);
  if (!actualWeight || actualWeight <= 0 || !actualReps || actualReps <= 0 || !targetReps || targetReps <= 0) {
    return currentRm12 > 0 ? roundToPlate(currentRm12, unit, step) : 0;
  }
  const adj = dupAdjustment(targetReps, actualReps, unit, step);
  if (adj === 0) return currentRm12;
  const newTrainedWeight = Math.max(step, actualWeight + adj);
  return rm12FromUserInput(newTrainedWeight, targetReps, unit, step);
}

/**
 * Returns truthy when DUP is enabled on this exercise. Note: pre-Feb
 * 2026 this also required `exercise.dup.rm12 > 0`, but rm12 has since
 * moved to the plan-level `dupExerciseRm12` map. Now the only signal
 * needed is `enabled: true`. The caller passes the plan to
 * `applyDupToExercise` which reads the actual rm12 from there.
 *
 * Back-compat: when the per-exercise legacy `rm12` is still present we
 * surface it in the returned config so callers that historically read
 * `cfg.rm12` keep working.
 */
export function getExerciseDupConfig(exercise: any): DupConfig | null {
  const cfg = exercise?.dup;
  if (!cfg || !cfg.enabled) return null;
  // Treat rm12 of 0 as "not yet known" rather than "disabled". Callers
  // resolve the actual rm12 via `getDupRm12(plan, exercise)`.
  const legacyRm12 = typeof cfg.rm12 === 'number' && cfg.rm12 > 0 ? cfg.rm12 : 0;
  return {
    enabled: true,
    mode: cfg.mode || 'dup-3',
    rm12: legacyRm12,
    unit: cfg.unit || 'kg',
    targetReps: typeof cfg.targetReps === 'number' && cfg.targetReps > 0 ? cfg.targetReps : undefined,
    lastUpdated: cfg.lastUpdated,
  };
}

// -- Plan-level RM map helpers -------------------------------------------
// rm12 lives on the plan, keyed by exercise NAME. This means:
//   • The same lift on multiple training days reads/writes ONE rm12.
//   • Renaming a lift creates a new key (correct — it's a new lift).
//   • Two genuinely-distinct lifts with the same name collide. Acceptable
//     because the user's UI doesn't let two lifts share a name on the
//     same plan today; we'll address that with `dup.exerciseKey` later
//     if the use-case emerges.

interface PlanRm12Entry {
  rm12: number;
  unit: 'kg' | 'lb';
  loadIncrement?: number;
  lastUpdated: string;
}

function exerciseKey(exercise: any): string | null {
  const n = exercise?.name;
  return typeof n === 'string' && n.trim().length > 0 ? n.trim() : null;
}

/**
 * Read the canonical 12RM for this exercise.
 *
 * Resolution order:
 *   1. `plan.dupExerciseRm12[exercise.name]` — current canonical store.
 *   2. Legacy `exercise.dup.rm12` — read-only fallback for pre-Feb 2026
 *      plans. If we hit this branch we LAZILY MIGRATE: the next call to
 *      `setDupRm12` will populate the plan-level map. We DO NOT auto-
 *      migrate on read because reads shouldn't mutate the plan — see
 *      `migrateDupRm12ToPlan(plan)` for an explicit migration helper.
 *   3. 0 — exercise has never been DUP-configured. The caller treats
 *      this as "no prefill available".
 */
export function getDupRm12(plan: any, exercise: any): number {
  const key = exerciseKey(exercise);
  const map = plan?.dupExerciseRm12;
  if (key && map && typeof map === 'object') {
    const entry = map[key];
    if (entry && typeof entry.rm12 === 'number' && entry.rm12 > 0) {
      return entry.rm12;
    }
  }
  const legacy = Number(exercise?.dup?.rm12);
  if (legacy > 0) return legacy;
  return 0;
}

/**
 * Read the full rm12 record (rm12 + unit + last-updated) with fallback
 * to the per-exercise legacy fields. Useful when callers need the unit
 * or timestamp alongside the value.
 */
export function getDupRm12Entry(plan: any, exercise: any): PlanRm12Entry | null {
  const key = exerciseKey(exercise);
  const map = plan?.dupExerciseRm12;
  if (key && map && typeof map === 'object') {
    const entry = map[key];
    if (entry && typeof entry.rm12 === 'number' && entry.rm12 > 0) {
      return {
        rm12: entry.rm12,
        unit: (entry.unit === 'lb' ? 'lb' : 'kg'),
        loadIncrement: typeof entry.loadIncrement === 'number' ? entry.loadIncrement : undefined,
        lastUpdated: entry.lastUpdated || new Date(0).toISOString(),
      };
    }
  }
  const legacyRm12 = Number(exercise?.dup?.rm12);
  if (legacyRm12 > 0) {
    return {
      rm12: legacyRm12,
      unit: exercise?.dup?.unit === 'lb' ? 'lb' : 'kg',
      loadIncrement: typeof exercise?.loadIncrement === 'number' ? exercise.loadIncrement : undefined,
      lastUpdated: exercise?.dup?.lastUpdated || new Date(0).toISOString(),
    };
  }
  return null;
}

/**
 * Write the canonical 12RM to the plan-level map. Mutates `plan` in
 * place. The per-exercise legacy `exercise.dup.rm12` is INTENTIONALLY
 * NOT mirrored here — keeping it stale ensures `getDupRm12` always
 * prefers the plan-level truth and we avoid the "two values drifting
 * apart per training day" bug that prompted this refactor.
 *
 * The only field we still mirror onto the exercise is `lastUpdated`,
 * so the UI badge that shows "RM updated 2h ago" keeps working without
 * needing the plan ref.
 */
export function setDupRm12(
  plan: any,
  exercise: any,
  rm12: number,
  opts: { unit?: 'kg' | 'lb'; loadIncrement?: number } = {},
): void {
  if (!plan || !exercise) return;
  const key = exerciseKey(exercise);
  if (!key || rm12 <= 0) return;
  if (!plan.dupExerciseRm12 || typeof plan.dupExerciseRm12 !== 'object') {
    plan.dupExerciseRm12 = {};
  }
  const now = new Date().toISOString();
  plan.dupExerciseRm12[key] = {
    rm12,
    unit: opts.unit || exercise?.dup?.unit || 'kg',
    loadIncrement: opts.loadIncrement || exercise?.loadIncrement,
    lastUpdated: now,
  };
  // Best-effort: bump the exercise's lastUpdated for UI display. We
  // intentionally do NOT write rm12 back onto the exercise — keeping
  // it stale is part of the design (see comment above).
  if (exercise.dup) {
    exercise.dup = { ...exercise.dup, lastUpdated: now };
  }
}

/**
 * One-shot migration: for every exercise in every day, copy the legacy
 * `exercise.dup.rm12` into `plan.dupExerciseRm12[name]` (if absent),
 * picking the most recently updated value when multiple days have the
 * same exercise. Idempotent — safe to call on every plan load.
 *
 * Mutates `plan` in place. Returns true if at least one entry was
 * migrated, false if there was nothing to do. The caller is responsible
 * for persisting the plan after migration.
 */
export function migrateDupRm12ToPlan(plan: any): boolean {
  if (!plan?.dayExercises) return false;
  if (!plan.dupExerciseRm12 || typeof plan.dupExerciseRm12 !== 'object') {
    plan.dupExerciseRm12 = {};
  }
  let migrated = false;
  // Phase 1 — collect best (most-recent-or-highest) legacy rm12 per name.
  const candidates: Record<string, PlanRm12Entry> = {};
  Object.keys(plan.dayExercises).forEach(dayKey => {
    const list = plan.dayExercises[dayKey] || [];
    list.forEach((ex: any) => {
      const key = exerciseKey(ex);
      const legacyRm12 = Number(ex?.dup?.rm12);
      if (!key || !ex?.dup?.enabled || !(legacyRm12 > 0)) return;
      const lastUpdated = ex?.dup?.lastUpdated || new Date(0).toISOString();
      const current = candidates[key];
      if (!current || lastUpdated > current.lastUpdated || (lastUpdated === current.lastUpdated && legacyRm12 > current.rm12)) {
        candidates[key] = {
          rm12: legacyRm12,
          unit: ex?.dup?.unit === 'lb' ? 'lb' : 'kg',
          loadIncrement: typeof ex?.loadIncrement === 'number' ? ex.loadIncrement : undefined,
          lastUpdated,
        };
      }
    });
  });
  // Phase 2 — populate plan-level map for any missing keys.
  Object.keys(candidates).forEach(key => {
    if (!plan.dupExerciseRm12[key] || !(Number(plan.dupExerciseRm12[key].rm12) > 0)) {
      plan.dupExerciseRm12[key] = candidates[key];
      migrated = true;
    }
  });
  return migrated;
}

// ============================================================================
// v3 per-(exercise, rep-range) working-weight API — Feb 2026
// ============================================================================
//
// The canonical store. Replaces the rm12 model with one slot per
// (exerciseName, targetReps). No Epley cross-projection — every rep
// range tracks independently from its own Set 1 history.
//
//   plan.dupExerciseWeights = {
//     "Bench Press": {
//       "6":  { weight: 92.5, inc: 1.25, lastUpdated: "..." },
//       "12": { weight: 80,   inc: 1.25, lastUpdated: "..." },
//       "25": { weight: 65,   inc: 1.25, lastUpdated: "..." },
//     },
//     ...
//   }

interface PlanDupWeightEntry {
  weight: number;
  inc: number;
  sets?: number;        // user's chosen set count for THIS rep range
  lastUpdated: string;
}

function repKey(targetReps: number | null | undefined): string | null {
  if (typeof targetReps !== 'number' || !(targetReps > 0)) return null;
  return String(targetReps);
}

/** Read the working weight for this (exercise, target reps) slot. Returns
 *  0 when there's no entry — caller may fall back to legacy rm12 derivation. */
export function getDupWeight(plan: any, exercise: any, targetReps: number | null | undefined): number {
  const exKey = exerciseKey(exercise);
  const tKey = repKey(targetReps);
  if (!exKey || !tKey) return 0;
  const slot = plan?.dupExerciseWeights?.[exKey]?.[tKey];
  if (slot && typeof slot.weight === 'number' && slot.weight > 0) return slot.weight;
  return 0;
}

/** Write the working weight. Mutates `plan` in place. The exercise's
 *  `dup.lastUpdated` is bumped for the UI "updated 2h ago" badge.
 *  `opts.sets` persists the user's chosen set count for THIS rep range
 *  so applyDupToExercise no longer force-grows the set count from a
 *  hardcoded default. */
export function setDupWeight(
  plan: any,
  exercise: any,
  targetReps: number,
  weight: number,
  opts: { inc?: number; sets?: number } = {},
): void {
  if (!plan || !exercise) return;
  const exKey = exerciseKey(exercise);
  const tKey = repKey(targetReps);
  if (!exKey || !tKey || !(weight > 0)) return;
  if (!plan.dupExerciseWeights || typeof plan.dupExerciseWeights !== 'object') {
    plan.dupExerciseWeights = {};
  }
  if (!plan.dupExerciseWeights[exKey] || typeof plan.dupExerciseWeights[exKey] !== 'object') {
    plan.dupExerciseWeights[exKey] = {};
  }
  const now = new Date().toISOString();
  const prev = plan.dupExerciseWeights[exKey][tKey] || {};
  plan.dupExerciseWeights[exKey][tKey] = {
    weight,
    inc: opts.inc || prev.inc || exercise?.loadIncrement || 2.5,
    // Preserve previously-stored sets if caller doesn't supply one.
    sets: opts.sets && opts.sets > 0 ? opts.sets : prev.sets,
    lastUpdated: now,
  };
  if (exercise.dup) {
    exercise.dup = { ...exercise.dup, lastUpdated: now };
  }
}

/** Read the stored set count for this (exercise, rep-range) slot. Returns
 *  null when no slot exists or sets count wasn't recorded — caller falls
 *  back to existing exercise.sets.length or a sensible default. */
export function getDupSets(plan: any, exercise: any, targetReps: number | null | undefined): number | null {
  const exKey = exerciseKey(exercise);
  const tKey = repKey(targetReps);
  if (!exKey || !tKey) return null;
  const slot = plan?.dupExerciseWeights?.[exKey]?.[tKey];
  return slot && typeof slot.sets === 'number' && slot.sets > 0 ? slot.sets : null;
}

/**
 * v3 update rule. The user has accepted Mann's reps-based table as "OK
 * and not aggressive" for all three target zones (6 / 12 / 25 reps),
 * so the table itself is preserved. The two changes vs v1/v2:
 *   1. Anchor on the user's ACTUAL Set 1 weight, not the previously
 *      stored prescription. If they manually dialed weight up or down
 *      we honor that reality.
 *   2. When Mann delta === 0 (user landed in the "no-change" zone):
 *      next weight = actualWeight EXACTLY. No rounding through Epley,
 *      no rm12 round-trip drift. This is the half-step fix — the user
 *      can lift 23 kg on a 25-rep day and next session prescribes 23,
 *      not 22.5.
 */
export function dupNewWeightFromActual(
  currentWeight: number,
  targetReps: number,
  actualWeight: number,
  actualReps: number,
  unit: 'kg' | 'lb' = 'kg',
  inc?: number,
): number {
  const step = inc && inc > 0 ? inc : (unit === 'lb' ? LB_INCREMENT : KG_INCREMENT);
  if (!actualWeight || actualWeight <= 0 || !actualReps || actualReps <= 0 || !targetReps || targetReps <= 0) {
    return currentWeight > 0 ? currentWeight : 0;
  }
  const adj = dupAdjustment(targetReps, actualReps, unit, step);
  if (adj === 0) return actualWeight;
  return roundToIncrement(Math.max(step, actualWeight + adj), step);
}

/**
 * One-shot migration. For every DUP-enabled exercise, seed
 * `plan.dupExerciseWeights[name][targetReps]` from either:
 *   1. The exercise's CURRENT prescribed weight in dayExercises (most
 *      accurate — it reflects whatever the user last had on the bar
 *      after any week-of-week prior adjustment).
 *   2. The legacy rm12 → Epley projection at this rep range (fallback
 *      for plans where the prescription isn't materialized yet).
 *
 * Mutates `plan` in place. Idempotent — skips slots that already exist.
 */
export function migrateDupWeightsToPlan(plan: any): boolean {
  if (!plan?.dayExercises) return false;
  if (!plan.dupExerciseWeights || typeof plan.dupExerciseWeights !== 'object') {
    plan.dupExerciseWeights = {};
  }
  let migrated = false;

  // Walk every (day, exercise). Each appearance contributes its day's
  // rep target — that's our slot key.
  Object.keys(plan.dayExercises).forEach(dayKey => {
    const list = plan.dayExercises[dayKey] || [];
    list.forEach((ex: any) => {
      const name = exerciseKey(ex);
      if (!name || !ex?.dup?.enabled) return;
      const tr = getDupTargetRepsForExercise(plan, ex, dayKey);
      const tKey = repKey(tr);
      if (!tKey || !tr) return;
      const existing = plan.dupExerciseWeights[name]?.[tKey];
      if (existing && Number(existing.weight) > 0) return; // already migrated

      // Source 1: current set-1 prescription weight on the exercise.
      const inc = ex.loadIncrement || (ex?.dup?.unit === 'lb' ? LB_INCREMENT : KG_INCREMENT);
      let seedWeight = 0;
      const set1 = (ex.sets || [])[0];
      if (set1 && set1.weight !== undefined && set1.weight !== '' && set1.weight !== null) {
        const w = parseFloat(String(set1.weight));
        if (w > 0) seedWeight = w;
      }
      // Source 2: fallback to rm12 → Epley projection.
      if (!(seedWeight > 0)) {
        const rm12 = getDupRm12(plan, ex);
        if (rm12 > 0) {
          seedWeight = dupWorkingWeight(rm12, tr, ex?.dup?.unit || 'kg', inc);
        }
      }
      if (!(seedWeight > 0)) return;

      if (!plan.dupExerciseWeights[name]) plan.dupExerciseWeights[name] = {};
      // Capture the current set count too — preserves the user's choice
      // (e.g. a 2-set DUP-25 endurance day) so applyDupToExercise won't
      // force-grow it back to a hardcoded 3 on next render.
      const setsCount = Array.isArray(ex.sets) && ex.sets.length > 0 ? ex.sets.length : undefined;
      plan.dupExerciseWeights[name][tKey] = {
        weight: seedWeight,
        inc,
        ...(setsCount ? { sets: setsCount } : {}),
        lastUpdated: ex?.dup?.lastUpdated || new Date(0).toISOString(),
      };
      migrated = true;
    });
  });
  return migrated;
}

/**
 * Apply DUP prefill to an exercise. The PLANNED training day's target reps
 * drive the load and the visible rep count. Set 1 is always AMRAP ('max');
 * Sets 2..N use the target reps.
 *
 * `dayKey` is the training day this exercise belongs to (e.g. "Fri"), NOT
 * the system date. This is critical — users plan future days ahead, and
 * keying off real-time today would write/read the wrong slot.
 *
 * Behaviour when this dayKey has NO assignment in the schedule:
 *   - Do NOT touch the exercise's existing sets. The user will trigger
 *     the setup dialog (or the plan migrator) to assign this day's reps.
 */
/**
 * Apply DUP prefill to an exercise. The PLANNED training day's target reps
 * drive the prescribed weight. Set 1 is always AMRAP ('max'); Sets 2..N
 * use the target reps.
 *
 * `dayKey` is the training day this exercise belongs to (e.g. "Fri"), NOT
 * the system date.
 *
 * Weight resolution order (v3 — per-rep-range storage, Feb 2026):
 *   1. `plan.dupExerciseWeights[name][targetReps]` — canonical store.
 *   2. Back-compat: derive from legacy `rm12` via Epley (only on first
 *      load before migration runs).
 *   3. Empty — exercise has never been logged at this rep range; leave
 *      sets alone so the user enters their own weight.
 */
export function applyDupToExercise(
  exercise: any,
  plan: any,
  dayKey?: string,
  targetSets?: number,
  opts?: { keepSetCount?: boolean },
): void {
  const cfg = getExerciseDupConfig(exercise);
  if (!cfg) return;
  const targetReps = getDupTargetRepsForExercise(plan, exercise, dayKey);
  if (!targetReps) return; // No assignment → leave the user's setup alone.

  // v3 canonical: per-(exercise, rep-range) weight.
  let weight = getDupWeight(plan, exercise, targetReps);
  if (!(weight > 0)) {
    // Legacy fallback: derive from rm12 ONCE so existing plans don't
    // appear empty. Migration writes this back to the v3 store on next
    // setDone.
    const rm12 = getDupRm12(plan, exercise);
    if (rm12 > 0) {
      const inc = getExerciseIncrement(exercise, cfg.unit);
      weight = dupWorkingWeight(rm12, targetReps, cfg.unit, inc);
    }
  }
  if (!(weight > 0)) {
    // Iter276: no stored working weight yet (e.g. DUP just enabled, or a
    // fresh cycle before Set 1 is logged). Previously this returned early
    // and left EVERY reps field blank. The rep target doesn't depend on
    // the weight, so continue and prescribe reps only.
    weight = 0;
  }

  // Iter301 — DELOAD WEEK. APRE has been deload-aware since iter189
  // (`applyApreToExercise` scales the RM by 0.75), but DUP prescribed the
  // full canonical working weight. Any path that cleared a DUP set's
  // `weight` during a deload week (week roll-over, plan re-load, set
  // reset) therefore re-filled it at 100% — the header showed "DELOAD"
  // while the card showed normal weights, and the user's manually
  // triggered deload looked like it did nothing.
  // Covers the planned deload AND manually-triggered weeks.
  const cw = plan?.currentWeek;
  const inDeload = !!plan && (
    (plan.hasDeloadWeek && cw === plan.deloadWeek)
    || (Array.isArray(plan.manualDeloadWeeks) && plan.manualDeloadWeeks.includes(cw))
  );
  if (inDeload && weight > 0) {
    weight = Math.round(weight * 0.75 * 2) / 2;
  }

  // Resolve target set count. Priority:
  //   1. Explicit `targetSets` arg (rare — callers like resetForNewWeek)
  //   2. v3-stored sets count for this (exercise, rep-range) slot —
  //      remembers what the user chose in the setup dialog
  //   3. Existing exercise.sets.length — respects whatever count the
  //      plan currently has
  //   4. 3 — sensible last-resort default for a fresh DUP-enabled exercise
  let setsCount = targetSets;
  if (!setsCount || setsCount <= 0) {
    setsCount = getDupSets(plan, exercise, targetReps) || undefined;
  }
  if (!setsCount || setsCount <= 0) {
    setsCount = (exercise.sets && exercise.sets.length) || 3;
  }

  if (!exercise.sets) exercise.sets = [];
  // iter285 — `keepSetCount` callers (the plan-load self-heal) must never
  // resize: a set the user added by hand was being deleted on every load.
  if (!opts?.keepSetCount) {
    while (exercise.sets.length < setsCount) {
      exercise.sets.push({ done: false, type: 'Regular' });
    }
    while (exercise.sets.length > setsCount && !exercise.sets[exercise.sets.length - 1].done) {
      exercise.sets.pop();
    }
  }

  for (let i = 0; i < exercise.sets.length; i++) {
    const set = exercise.sets[i];
    if (set.done) continue;
    if (weight > 0 && (set.weight === undefined || set.weight === '' || set.weight === null)) {
      set.weight = String(weight);
    }
    if (set.reps === undefined || set.reps === '' || set.reps === null) {
      // Set 1 is AMRAP — stored EMPTY so the user must type the real
      // number; the card renders "max" as the placeholder hint. Sets 2+
      // carry this day's target rep range (e.g. 12 / 6 / 25).
      // (Production bug 2026-06-28: users were logging sets with
      // reps='max' because the seed value was treated as valid.)
      set.reps = i === 0 ? '' : String(targetReps);
    }
  }
}

/**
 * True when a set was logged as a myorep cluster (`Myoreps` or
 * `Myoreps Match`).
 *
 * Myoreps are an activation set plus rest-pause mini-sets logged as ONE
 * set, so the rep count is structurally much higher than a straight set at
 * the same load. Feeding that into Mann's table made DUP read it as a
 * massive AMRAP overshoot and add load every session. Owner rule
 * (Sep 2026): a myorep Set 1 must never raise the weight.
 */
export function isMyorepSet(set: any): boolean {
  return /myoreps/i.test(String(set?.type || ''));
}

/**
 * Called when a set is logged DONE in a DUP-enabled exercise. Updates the
 * per-(exercise, rep-range) working weight based on Set 1's ACTUAL logged
 * weight and reps. See `dupNewWeightFromActual` for the rule.
 *
 * `dayKey` must be the training day key the user is logging.
 *
 * Safe to call repeatedly for the same logged set: the new weight is
 * derived from Set 1's ACTUAL weight + reps, never from the previously
 * stored value, so re-running is idempotent. `updateSetType` relies on
 * that to re-evaluate when the set type changes after logging.
 */
export function handleDupSetDone(plan: any, exercise: any, setIndex: number, dayKey?: string): boolean {
  const cfg = getExerciseDupConfig(exercise);
  if (!cfg || !exercise.sets || setIndex !== 0) return false;
  const targetReps = getDupTargetRepsForExercise(plan, exercise, dayKey);
  if (!targetReps) return false;

  // iter251d: DO NOT update the canonical DUP working-weight store during
  // a deload week (planned OR manually-triggered). Set 1's weight during
  // deload is 75% of normal — writing that back to `dupExerciseWeights`
  // would permanently shrink the user's stored weight and next week's
  // prefill would come back at 56% (75% × 75%). Matches APRE's design
  // which skips its RM update during deload (see updateApreForDay).
  const cw = plan?.currentWeek;
  const inDeloadWeek = (plan?.hasDeloadWeek && cw === plan?.deloadWeek)
    || (Array.isArray(plan?.manualDeloadWeeks) && plan.manualDeloadWeeks.includes(cw));
  if (inDeloadWeek) return false;

  const set1 = exercise.sets[0];
  // Iter308 — PARTIALS MUST NOT DRIVE PROGRESSION.
  // Owner bug (Sep 2026): "I made 2 sets DUP-12, did 12 reps, and the app
  // added weight anyway." His export proves it: 19 Sep Barbell Curl Set 1 =
  // 36 kg × 12 full + 4 HALF reps → effective 14 → Mann delta +2 → +2.5 kg
  // (stored 37.5); the triceps extension 38 kg × 12 + 3 halves did the same
  // (stored 40). Lengthened partials are a fatigue tool, not proof the load
  // is too light, so the load decision now uses FULL reps only. Half-reps
  // still count everywhere else (volume, statistics, AI analysis).
  const reps = Math.round(parseFloat(String(set1?.reps ?? '')) || 0);
  const weight = parseFloat(String(set1?.weight || '0'));
  if (!reps || reps <= 0 || !weight || weight <= 0) return false;

  const inc = getExerciseIncrement(exercise, cfg.unit);
  const currentWeight = getDupWeight(plan, exercise, targetReps);
  let newWeight = dupNewWeightFromActual(currentWeight, targetReps, weight, reps, cfg.unit, inc);
  // Myorep clamp: never let a myorep Set 1 ADD load. Clamping to the weight
  // actually used means a positive Mann adjustment collapses to "same
  // weight again", while a NEGATIVE adjustment still passes through — a
  // genuinely too-heavy load keeps self-correcting downwards.
  if (isMyorepSet(set1)) {
    newWeight = Math.min(newWeight, weight);
  }
  setDupWeight(plan, exercise, targetReps, newWeight, { inc });
  return false;
}

/** Reset DUP sets for a new week — clears values so the week's next
 *  session re-prefills using the (updated) 12RM. */
export function resetDupSetsForNewWeek(exercise: any): void {
  const cfg = getExerciseDupConfig(exercise);
  if (!cfg || !exercise.sets) return;
  for (let i = 0; i < exercise.sets.length; i++) {
    exercise.sets[i] = { done: false, type: 'Regular' };
  }
}

/** Convenience: how to describe today for display badges. */
export function getDupDayLabel(targetReps: number | null): string {
  if (!targetReps) return 'Unassigned';
  if (targetReps <= 6) return 'Strength';
  if (targetReps <= 15) return 'Hypertrophy';
  return 'Endurance';
}

export const DUP_INFO_TEXT =
  'DUP-3 (Daily Undulating Periodization): each training weekday is assigned a rep range (default 12 / 6 / 25). Week 1 you set them up by hand; from week 2 the same weekday automatically uses the same rep range, with weights silently adjusted after every Set 1 (AMRAP).';

// ---------------------------------------------------------------------------
// Legacy exports kept so older imports/tests don't break. The new code
// should use `getDupTargetRepsForToday` and the weight helpers above.
// ---------------------------------------------------------------------------
export type DupRotation = 0 | 1 | 2;
export interface DupDay {
  rotation: DupRotation;
  label: 'Hypertrophy' | 'Strength' | 'Endurance';
  shortLabel: string;
  targetReps: number;
  loadPctOf12rm: number;
}
const LEGACY_ROTATIONS: Record<DupRotation, DupDay> = {
  0: { rotation: 0, label: 'Hypertrophy', shortLabel: '12-rep', targetReps: 12, loadPctOf12rm: 1.0 },
  1: { rotation: 1, label: 'Strength',    shortLabel: '6-rep',  targetReps: 6,  loadPctOf12rm: 1.17 },
  2: { rotation: 2, label: 'Endurance',   shortLabel: '25-rep', targetReps: 25, loadPctOf12rm: 0.78 },
};
export function getDupDay(rotation: DupRotation): DupDay { return LEGACY_ROTATIONS[rotation]; }
export function getPlanDupRotation(plan: any): DupRotation {
  // Back-compat shim — kept only for stale imports. Returns a rotation
  // index that vaguely corresponds to today's system-date target reps.
  // New code should call `getDupTargetRepsForDay(plan, dayKey)` and pass
  // the actual training day, not infer from `Date.now()`.
  const todayReps = getDupTargetRepsForDay(plan, todayWeekdayKey());
  if (todayReps === 12) return 0;
  if (todayReps === 6) return 1;
  if (todayReps === 25) return 2;
  const r = plan?.dupRotation;
  if (r === 1 || r === 2) return r;
  return 0;
}
